import { NODE_ENV, DEPLOY_TARGET } from '../config/constants';
import { getPagePermission } from './utils';
import { ensureMfeUiShared } from '../mfeSharedRuntime';

/** Page keys that render the embedded stock microfrontends (admin or operations shell). */
const STOCK_MFE_PAGE_KEYS = [
  'stockApp',
  'stockSetup',
  'stockSettings',
  'stockDeductionRouting',
];

export const ADMIN_STOCK_LOADER_MARKER = 'admin-stock-loader.js';
export const OPERATIONS_STOCK_LOADER_MARKER = 'operations-stock-loader.js';

// Stock microfrontend loader scripts per environment. NODE_ENV is 'development'
// only for `npm start`; staging and production both build as 'production', so
// DEPLOY_TARGET (injected at build time) selects the right CDN.
const STOCK_LOADER_URLS = {
  admin: {
    development: 'http://localhost:3001/admin-stock-loader.js',
    staging: 'https://d3p4ulpztjstyj.cloudfront.net/admin-stock-loader.js',
    production: 'https://d1xl26hakrlbb5.cloudfront.net/admin-stock-loader.js',
  },
  operations: {
    development: 'http://localhost:3050/operations-stock-loader.js',
    staging: 'https://d23pu833y3p6km.cloudfront.net/operations-stock-loader.js',
    production: 'https://dsj24aioky580.cloudfront.net/operations-stock-loader.js',
  },
};

function resolveStockEnv() {
  if (NODE_ENV === 'development') return 'development';
  return DEPLOY_TARGET === 'production' ? 'production' : 'staging';
}

export function getAdminStockLoaderScriptUrl() {
  return STOCK_LOADER_URLS.admin[resolveStockEnv()];
}

export function getOperationsStockLoaderScriptUrl() {
  return STOCK_LOADER_URLS.operations[resolveStockEnv()];
}

/**
 * iOS Safari (and low-RAM Android) enforces a per-tab memory budget; exceeding it
 * crash-reloads the tab ("A problem repeatedly occurred"). Preloading still pulls
 * a large MFE payload (even when React/MUI are shared with the host), so on these
 * devices we skip it and let each shell's on-demand loader fetch only the MFE the
 * user actually opens.
 */
export function isMemoryConstrainedDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    // iPadOS 13+ reports as Macintosh but has touch support
    (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);
  if (isIOS) return true;
  const mem = navigator.deviceMemory;
  return typeof mem === 'number' && mem > 0 && mem <= 4;
}

export function hasActiveStockMfePage(permissionTree) {
  if (!permissionTree?.length) return false;
  return STOCK_MFE_PAGE_KEYS.some((key) => {
    const page = getPagePermission(permissionTree, key);
    return page?.isActive;
  });
}

export function appendAdminStockLoaderScriptIfNeeded() {
  if (typeof document === 'undefined') return;
  if (customElements.get('admin-stock-app')) return;
  if (document.querySelector(`script[src*="${ADMIN_STOCK_LOADER_MARKER}"]`)) return;
  ensureMfeUiShared()
    .then(() => {
      if (customElements.get('admin-stock-app')) return;
      if (document.querySelector(`script[src*="${ADMIN_STOCK_LOADER_MARKER}"]`)) return;
      const script = document.createElement('script');
      script.src = getAdminStockLoaderScriptUrl();
      script.async = true;
      script.onerror = () => {
        console.warn('Failed to preload admin stock app script');
      };
      document.body.appendChild(script);
    })
    .catch((err) => {
      console.warn('Failed to prepare shared UI runtime for admin stock preload', err);
    });
}

export function appendOperationsStockLoaderScriptIfNeeded() {
  if (typeof document === 'undefined') return;
  if (customElements.get('operations-stock-app')) return;
  if (document.querySelector(`script[src*="${OPERATIONS_STOCK_LOADER_MARKER}"]`)) return;
  ensureMfeUiShared()
    .then(() => {
      if (customElements.get('operations-stock-app')) return;
      if (document.querySelector(`script[src*="${OPERATIONS_STOCK_LOADER_MARKER}"]`)) return;
      const script = document.createElement('script');
      script.src = getOperationsStockLoaderScriptUrl();
      script.async = true;
      script.onerror = () => {
        console.warn('Failed to preload operations stock app script');
      };
      document.body.appendChild(script);
    })
    .catch((err) => {
      console.warn('Failed to prepare shared UI runtime for operations stock preload', err);
    });
}
